/**
 * @file EULAPage.js
 * @description This file contains the EULAPage component, which allows users to review and agree to the Terms of Use.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getOrgLevelsForType,
  updateOrgLevelForTypeAndID,
  createOrgLevelForType,
} from "../../services/orgLevelService";
import styles from "../population-groups/PopulationGroup.module.scss";
import { SecurityAttributes } from "../../utils/data/enums";
import { useAuthContext } from "../../store/AuthContext";
import Toast from "../ui/toasts/Toast";
import { ROUTE_BUILDERS } from '../../constants/routes';
import { orgLevelNameFromType, makeTypeSingular } from "./OrgLevelCommon";
import { getText } from '../../i18n';

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem("tenantId");

const OrgLevelsList = () => {
  const navigate = useNavigate();
  let { orgLevelType, orgLevelID } = useParams();
  const [orgLevels, setOrgLevels] = useState([]);
  const [orgLevelBeingEdited, setOrgLevelBeingEdited] = useState({
    name: "",
    code: "",
    isActive: true,
    tenantId: getTenantId(),
  });
  const { hasAttribute } = useAuthContext();

  const saveOrgLevel = async () => {
    let response = null;

    if (orgLevelID === "new") {
      response = await createOrgLevelForType(orgLevelType, orgLevelBeingEdited);
    } else {
      //get this org level
      let orgLevel = orgLevels.filter((orgLevelToFilter) => {
        return `${orgLevelToFilter.id}` === `${orgLevelID}`;
      })[0];

      response = await updateOrgLevelForTypeAndID(
        orgLevelType,
        orgLevelID,
        orgLevel
      );
    }

    if (response.error) {
      Toast.error(response.message);
    } else if (response.status === 200) {
      Toast.success(`${makeTypeSingular(orgLevelNameFromType(orgLevelType))} saved successfully`);
      navigate(ROUTE_BUILDERS.orgLevels(orgLevelType));
    } else {
      Toast.error(`Failed to create the ${makeTypeSingular(orgLevelNameFromType(orgLevelType))}`);
    }
  };

  const close = () => {
    navigate(ROUTE_BUILDERS.orgLevels(orgLevelType));
  };

  useEffect(() => {
    getOrgLevelsForType(orgLevelType).then((response) => {
      setOrgLevels(response);
    });
  }, []);

  useEffect(() => {
    orgLevels.forEach((orgLevel) => {
      if (`${orgLevel.id}` === `${orgLevelID}`) {
        setOrgLevelBeingEdited(orgLevel);
      }
    });
  }, [orgLevels, orgLevelID]);

  const changeKeyForOrgLevelBeingEdited = (orgLevelID, key, value) => {
    if (orgLevelID === "new") {
      orgLevelBeingEdited[key] = value.trim();
      setOrgLevelBeingEdited(orgLevelBeingEdited);
    } else {
      //modify this org level
      let orgLevelsBeingEdited = orgLevels.map((orgLevelToSearch) => {
        if (`${orgLevelToSearch.id}` === `${orgLevelID}`) {
          orgLevelToSearch[key] = value.trim();
          setOrgLevelBeingEdited(orgLevelToSearch);
        }
        return orgLevelToSearch;
      });

      //set the new org levels
      setOrgLevels(orgLevelsBeingEdited);
    }
  };

  return (
    <div>
      {/* Creating a new org level */}
      {
        <div className="page-content">
          <div className="card">
            <div className="card-body px-0">
              <div className="d-flex justify-content-between align-items-center">
                <p className="page-title float-start ms-4">
                  {orgLevelID === "new" ? "Add New" : "Edit"}{" "}
                  {makeTypeSingular(orgLevelNameFromType(orgLevelType))}
                </p>
                <div className="float-end align-items-center me-4 d-sm-block d-lg-none ">
                  <p
                    type="button"
                    onClick={saveOrgLevel}
                    className="ing-text-buttons d-inline me-3"
                  >
                    <i className="bi bi-check-lg me-2" />
                    {getText('msg_efc007a393f6')}
                  </p>
                  <p
                    type="button"
                    onClick={close}
                    className="ing-text-buttons d-inline ms-3"
                  >
                    <i className="bi bi-x me-2" />
                    {getText('msg_bbfa773e5a63')}
                  </p>
                </div>
              </div>
              {/*
               *
               * Begin form body
               *
               */}
              <form
                className={`form-container p-3 ${styles.groupForm}`}
                style={{ width: "567px", margin: "auto" }}
              >
                <div className={`form-group mb-3`}>
                  <label htmlFor="groupName">
                    {getText('msg_709a23220f2c')}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    onChange={(event) => {
                      changeKeyForOrgLevelBeingEdited(
                        orgLevelID,
                        "name",
                        event.target.value
                      );
                    }}
                    maxLength="128"
                    defaultValue={orgLevelBeingEdited.name}
                    className={`form-control ${styles.nameClassName}`}
                    autoFocus
                  />
                </div>
                <div className={`form-group mb-3`}>
                  <label htmlFor="groupName">
                    {getText('msg_adac69379a62')}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    onChange={(event) => {
                      changeKeyForOrgLevelBeingEdited(
                        orgLevelID,
                        "code",
                        event.target.value
                      );
                    }}
                    maxLength="128"
                    className={`form-control ${styles.nameClassName}`}
                    defaultValue={orgLevelBeingEdited.code}
                    disabled={orgLevelID !== "new"}
                  />
                </div>
              </form>
              {/*
               *
               * End form body
               *
               */}
              <div className="text-center align-items-center me-4 d-lg-block d-sm-none">
                <p
                  type="button"
                  onClick={close}
                  className="ing-text-buttons d-inline me-3"
                >
                  <i className="bi bi-x me-2" />
                  {getText('msg_77dfd2135f4d')}
                </p>
                {hasAttribute(
                  SecurityAttributes.OrganizationLevelsFullAccess
                ) && (
                    <p
                      type="button"
                      onClick={saveOrgLevel}
                      className="ing-text-buttons d-inline ms-3"
                    >
                      <i className="bi bi-check-lg me-2" />
                      {getText('msg_efc007a393f6')}
                    </p>
                  )}
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default OrgLevelsList;
